import trackingEvents from "./trackingEvents";
import algoliasearch from "algoliasearch";

const DEBUG = true;
let currentTrackingContext = {};

/* Enhanced logging */
const log = (message, data = {}, type = "log") => {
  if (DEBUG) {
    console[type](`[Tracking] ${message}`, data);
  }
};

export const initTrackingContext = (context = {}) => {
  currentTrackingContext = {
    ...currentTrackingContext,
    ...context,
  };
};

/* Active section detection */
const getActiveSection = () => {
  try {
    const activeSelectors = [
      "header a.active",
      ".nav-link.active",
      '[aria-current="page"]',
      '[data-active="true"]',
    ];

    const activeElement = document.querySelector(activeSelectors.join(", "));
    if (activeElement) {
      return activeElement.textContent
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
    }

    const { pathname } = window.location;
    return pathname.split("/")[1] || "home";
  } catch (error) {
    log("Section detection failed", error, "error");
    return "unknown";
  }
};

/* Element data extraction */
const getElementData = (element) => {
  const data = {
    element,
    text: element.textContent,
    href: element.href || null,
    page_url: window.location.href,
    page_path: getActiveSection(),
    element_type: element.tagName.toLowerCase(),
    classes: element.className || "",
    attributes: {},
    dataset: {},
  };

  Array.from(element.attributes).forEach((attr) => {
    if (attr.name.startsWith("data-")) {
      const key = attr.name.replace("data-", "");
      data.attributes[key] = attr.value;
      data.dataset[key] = attr.value;
    }
  });

  return data;
};

/* Improved Job Tile detection */
const isJobTileLink = (elementData) => {
  try {
    if (!elementData.href) return false;

    const url = new URL(elementData.href);
    return (
      url.hostname.includes("jobs.pga.org") &&
      url.pathname.includes("/detail/") &&
      /^\/detail\/[a-zA-Z0-9]{18}$/.test(url.pathname)
    );
  } catch {
    return false;
  }
};

const isEventTileLink = (elementData) => {
  try {
    if (!elementData.href) return false;

    const url = new URL(elementData.href);
    return (
      url.hostname.includes("events") &&
      url.hostname.includes("pga") &&
      url.pathname.includes("/event/")
    );
  } catch {
    return false;
  }
};

const isSearchPGAEvents = (elementData) => {
  return (
    elementData.element_type === "button" &&
    (elementData.text.includes("Search") ||
      elementData.text.includes("VIEW ALL EVENTS"))
  );
};

/* Event matching logic */
const matchEventForElement = (element, elementData) => {
  // 1. Check explicit track-event attribute
  if (elementData.attributes["track-event"]) {
    const matchedEvent = trackingEvents.events.find(
      (e) => e.eventName === elementData.attributes["track-event"],
    );
    if (matchedEvent) return matchedEvent;
  }
  // 2. Special cases
  if (isSavePreferencesButton(elementData)) {
    return getEvent("Save Job Preferences");
  }
  if (isJobTileLink(elementData)) return getEvent("Click Job Tile");
  if (isEventTileLink(elementData)) return getEvent("Click Event Tile");
  if (isSearchPGAEvents(elementData)) syncFilters();
  if (isClickRegisterForEvent(elementData)) {
    return getEvent("Click Register For Event");
  }

  // 3. Known patterns
  for (const event of trackingEvents.events) {
    if (matchesEventPattern(element, elementData, event)) {
      return event;
    }
  }
  // 4. Default for clickable elements
  if (isClickableElement(element)) return getEvent("Click Link");

  return null;
};

/* Helper functions */
const getEvent = (eventName) => {
  return (
    trackingEvents.events.find((e) => e.eventName === eventName) || {
      eventName,
      properties: [
        { name: "page_url" },
        { name: "page_path" },
        { name: "text" },
        { name: "href" },
        { name: "element_type" },
      ],
    }
  );
};

const isClickRegisterForEvent = (elementData) => {
  return (
    elementData.href && elementData.text.toLowerCase().includes("register")
  );
};

const isSavePreferencesButton = (elementData) => {
  return (
    elementData.element_type === "button" &&
    (elementData.text.includes("save preferences") ||
      elementData.text.includes("guardar preferencias"))
  );
};

const isClickableElement = (element) => {
  return element.matches('a, button, [role="button"]');
};

/* Pattern matching */
const matchesEventPattern = (element, elementData, event) => {
  const { text, classes } = elementData;

  switch (event.eventName) {
    case "Click Nav Menu Item":
      return element.closest("nav, header") !== null;

    case "Click Log in":
      return text.includes("log in") || text.includes("sign in");

    case "Click Get Started":
      return text.includes("get started");

    case "Click News Tile":
      return classes.includes("news-tile");

    default:
      return false;
  }
};

const trackClickEventTile = (event) => {
  window.analytics.track("Click Event Tile", {
    event_title: event.title,
    event_start_date: event.start_datetime,
    event_end_date: event.end_datetime,
    event_location: event.city + " " + event.state,
    page_url: window.location.href,
    page_path: window.location.href,
  });
};

const trackClickRegisterForEvent = (event, url) => {
  window.analytics.track("Click Register For Event", {
    event_name: event.title,
    destination_url: url,
    page_url: window.location.href,
    page_path: window.location.href,
  });
};

/* Property collection */
const collectEventProperties = (event, elementData) => {
  const props = {
    page_url: elementData.page_url,
    page_path: elementData.page_path,
    ...currentTrackingContext,
  };

  if (event.eventName === "Click Link") {
    try {
      props.destination_url = elementData.href;
      props.link_text = elementData.text;
    } catch (error) {
      log("Failed to extract link props", error, "error");
    }
  }

  if (event.eventName === "Click Nav Menu Item") {
    try {
      props.destination_url = elementData.href;
      props.link_text = elementData.text;
      props.menu_type = "Main Nav";
      props.nav_item = elementData.text;
    } catch (error) {
      log("Failed to extract nav menu props", error, "error");
    }
  }

  // Special handling for Job Tile
  if (event.eventName === "Click Job Tile" && elementData.href) {
    try {
      const url = new URL(elementData.href);
      props.job_id = url.pathname.split("/").pop();
    } catch (error) {
      log("Failed to extract job ID", error, "error");
    }
  }

  if (event.eventName === "Click Event Tile") {
    try {
      const algoliaClient = algoliasearch(
        process.env.REACT_APP_ALGOLIA_APP_ID,
        process.env.REACT_APP_ALGOLIA_API_KEY,
      ).initIndex(process.env.REACT_APP_ALGOLIA_INDEX_NAME);
      algoliaClient
        .search("", {
          filters: `slug:"${elementData.href.match("(?<=event/).*$")[0]}"`,
          distinct: true,
          hitsPerPage: 200,
        })
        .then((result) => {
          trackClickEventTile(result.hits[0]);
        });
      props.dont_send = true;
    } catch (error) {
      log("Failed to extract event properties", error, "error");
    }
  }

  if (event.eventName === "Search PGA Events") {
    try {
      props.event_search_query = document.querySelector(
        '[placeholder="Search Events ..."',
      ).value;
    } catch (error) {
      log("Failed to extract event properties", error, "error");
    }
  }

  if (event.eventName === "Click Register For Event") {
    try {
      const algoliaClient = algoliasearch(
        process.env.REACT_APP_ALGOLIA_APP_ID,
        process.env.REACT_APP_ALGOLIA_API_KEY,
      ).initIndex(process.env.REACT_APP_ALGOLIA_INDEX_NAME);
      algoliaClient
        .search("", {
          filters: `slug:"${window.location.href.match("(?<=event/).*$")[0]}"`,
          distinct: true,
          hitsPerPage: 200,
        })
        .then((result) => {
          trackClickRegisterForEvent(result.hits[0], elementData.href);
        });
      props.dont_send = true;
    } catch (error) {
      log("Failed to extract event properties", error, "error");
    }
  }

  // Map other properties
  event.properties.forEach((prop) => {
    const value =
      elementData[prop.name] ||
      elementData.attributes[prop.name] ||
      elementData.dataset[prop.name];
    if (value !== undefined) {
      props[prop.name] = value;
    }
  });

  return props;
};

function trackRolexWidgetClick() {
  const iframe = document.querySelector("iframe");
  if (!iframe || !window.analytics?.track) return;

  const parent = iframe.parentElement;

  if (window.getComputedStyle(parent).position === "static") {
    parent.style.position = "relative";
  }

  const overlay = document.createElement("div");
  Object.assign(overlay.style, {
    position: "absolute",
    top: `${iframe.offsetTop}px`,
    left: `${iframe.offsetLeft}px`,
    width: `${iframe.offsetWidth}px`,
    height: `${iframe.offsetHeight}px`,
    backgroundColor: "transparent",
    zIndex: "9999",
    cursor: "pointer",
    pointerEvents: "auto",
  });

  overlay.addEventListener("click", () => {
    window.analytics.track("Click Rolex Icon", {
      page_url: window.location.href,
      page_path: getActiveSection(),
    });

    window.open(
      "https://www.rolex.com/?cmpid=dsp_Clock_pga.org/_2021_rlx2101379",
      "_blank",
    );
  });

  parent.appendChild(overlay);
}

/* Click handler */
const handleTrackClick = (event) => {
  if (event.button !== 0 || event.ctrlKey || event.metaKey) return;

  const targetElement = event.target.closest('a, button, [role="button"]');
  if (!targetElement) return;

  if (targetElement.getAttribute("href") === "/logout") {
    if (sessionStorage.getItem("segment_userId") && window.analytics) {
      sessionStorage.setItem("segment_userId", "anonymous");
      localStorage.setItem("last_identified_traits", JSON.stringify("{}"));
      window.analytics.reset();
      return;
    }
  }

  try {
    const elementData = getElementData(targetElement);
    const matchedEvent = matchEventForElement(targetElement, elementData);

    if (!matchedEvent) {
      return;
    }

    const properties = collectEventProperties(matchedEvent, elementData);
    delete properties.isLoggedIn;
    delete properties.user;
    if (window.analytics?.track && !properties.dont_send) {
      window.analytics.track(matchedEvent.eventName, properties);
    }
  } catch (error) {
    log("Tracking error", error, "error");
  }
};

function toSnakeCase(text) {
  return text.trim().toLowerCase().replace(/\s+/g, "_");
}

const getSearchResultsData = (props) => {
  props.event_search_query =
    document.querySelector('[placeholder="Search Events ..."]')?.value || "";
  const hitsList = document.querySelector(".ais-Hits-list");
  props.event_search_results_returned = hitsList
    ? hitsList.childElementCount
    : 0;
  if (document.querySelector("#location-zipcode")) {
    if (document.querySelector("#location-zipcode").value === "") {
      delete props["event_search_refinements"].search_city;
    } else {
      props["event_search_refinements"].search_city =
        document.querySelector("#location-zipcode").value;
    }
  }
  const radiusInput = document.querySelector(".form-inline select");
  if (radiusInput) {
    props["event_search_refinements"].radius = radiusInput.value;
  }
  window.analytics.track("Search PGA Events", props);
};

function getRefinements(event) {
  if (event.target.tagName === "INPUT") return;
  window._eventSearchRefinements = window._eventSearchRefinements || {};
  let refinements = window._eventSearchRefinements;

  const label = event.target.closest(".ais-RefinementList-label");
  if (!label) return;

  let wrapper = label.parentElement;
  while (wrapper && !wrapper.querySelector("h5")) {
    wrapper = wrapper.parentElement;
  }
  if (!wrapper) return;

  const h5Element = wrapper.querySelector("h5");
  const h5Text = h5Element ? toSnakeCase(h5Element.textContent.trim()) : "";
  if (!h5Text) return;

  const clickedText =
    event.target.textContent?.trim() ||
    label.querySelector(".ais-RefinementList-labelText")?.textContent?.trim() ||
    "";
  if (!clickedText) return;

  if (!Array.isArray(refinements[h5Text])) {
    refinements[h5Text] = [];
  }

  if (h5Text === "date_range") refinements[h5Text] = [];
  const list = refinements[h5Text];
  const index = list.indexOf(clickedText);

  if (index === -1) {
    list.push(clickedText);
  } else {
    list.splice(index, 1);
    if (list.length === 0) {
      delete refinements[h5Text];
    }
  }
  syncFilters();
}

function syncFilters() {
  let refinements = window._eventSearchRefinements;
  window.sessionStorage.setItem(
    "eventSearchRefinements",
    JSON.stringify(refinements),
  );
  const refinementsClone = JSON.parse(JSON.stringify(refinements));

  if (refinementsClone.length) {
    Object.keys(refinementsClone).forEach((key) => {
      const list = refinementsClone[key];
      const copy = [...list];

      copy.forEach((item) => {
        const itemExists = Array.from(
          document.querySelectorAll(".ais-RefinementList-labelText"),
        ).some((el) => el.textContent.trim() === item);

        if (!itemExists) {
          const index = list.indexOf(item);
          if (index > -1) {
            list.splice(index, 1);
          }
        }
      });

      if (list.length === 0) {
        delete refinementsClone[key];
      }
    });
  }

  getSearchResultsData({
    event_search_query:
      document.querySelector('[placeholder="Search Events ..."]')?.value || "",
    event_search_refinements: refinementsClone,
    event_search_results_returned: "",
  });
}

const getSearchRefinements = () => {
  const filtersContainer = document.querySelector(
    'div[class*="ais-InstantSearch__root"] > div:nth-child(2) > div > div:first-child',
  );
  if (filtersContainer && !filtersContainer.dataset.listenerAttached) {
    filtersContainer.dataset.listenerAttached = "true";

    filtersContainer.addEventListener("click", function (event) {
      setTimeout(() => {
        getRefinements(event);
      }, 2000);
    });

    const radiusInput = document.querySelector(".form-inline select");
    if (radiusInput) {
      radiusInput.addEventListener("change", function () {
        setTimeout(() => {
          syncFilters();
        }, 2000);
      });
    }
  }
};

export const trackViewEventsSearch = () => {
  const storedRefinements = window.sessionStorage.getItem(
    "eventSearchRefinements",
  );
  if (storedRefinements) {
    try {
      window._eventSearchRefinements = JSON.parse(storedRefinements);
    } catch (e) {
      window._eventSearchRefinements = {};
    }
  } else {
    window._eventSearchRefinements = {};
  }

  const initListener = (input) => {
    if (!input || input.dataset.listenerAttached === "true") return;

    input.dataset.listenerAttached = "true";
    window._eventSearchRefinements = window._eventSearchRefinements || {};
    window.latestInputValue = window.latestInputValue || "";
    window.debounceTimer = window.debounceTimer || null;

    if (window.location.href.includes("search")) {
      getSearchRefinements();
    }

    input.addEventListener("input", function (e) {
      window.latestInputValue = e.target.value;
      clearTimeout(window.debounceTimer);

      window.debounceTimer = setTimeout(() => {
        syncFilters();
      }, 2000);
    });
  };

  const titleInput = document.querySelector(
    '[placeholder="Search Events ..."]',
  );
  const cityInput = document.querySelector("#location-zipcode");

  if (titleInput) {
    initListener(titleInput);
  }

  if (cityInput) {
    initListener(cityInput);
  }

  const titleObserver = new MutationObserver(() => {
    if (titleInput) {
      initListener(titleInput);
      titleObserver.disconnect();
    }
  });

  const cityObserver = new MutationObserver(() => {
    if (cityInput) {
      initListener(cityInput);
      cityObserver.disconnect();
    }
  });

  titleObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  cityObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

/* Initialize tracking */
export const initTracking = () => {
  if (typeof window === "undefined") return;

  document.addEventListener("click", handleTrackClick, {
    capture: true,
    passive: true,
  });

  window.onload = () => {
    trackRolexWidgetClick();
  };
};
